import { formatDatumIso } from '../../utils/casUtils';
import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { csCZ } from '@mui/x-data-grid/locales';
import {
    Download as ExportIcon,
    Delete as CleanupIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import {
    useAuditLogsQuery,
    useAuditStatisticsQuery,
    useCleanupAuditLogsMutation,
} from '../../api/JsdhNotifikaceService/service/AdminMutations';
import AdminService from '../../api/JsdhNotifikaceService/service/AdminService';
import { useAuth } from '../../context/AuthContext';

// Překlad statusů do češtiny
const translateStatus = (status: string): string => {
    switch (status.toUpperCase()) {
        case 'UP':
            return 'V pořádku';
        case 'DOWN':
            return 'Nefunkční';
        case 'DEGRADED':
            return 'Zhoršený';
        case 'UNKNOWN':
            return 'Neznámý';
        case 'SUCCESS':
            return 'Úspěch';
        case 'FAILURE':
        case 'ERROR':
            return 'Chyba';
        default:
            return status;
    }
};

const AuditLogPage: React.FC = () => {
    const { isAdmin } = useAuth();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [filters, setFilters] = useState({
        action: '',
        entityType: '',
        dateFrom: '',
        dateTo: '',
    });
    const [openCleanupDialog, setOpenCleanupDialog] = useState(false);
    const [cleanupDays, setCleanupDays] = useState(90);

    const {
        data: auditLogs,
        isLoading,
        refetch,
    } = useAuditLogsQuery({
        ...filters,
        page,
        size: pageSize,
        action: filters.action || undefined,
        entityType: filters.entityType || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
    });

    const { data: statistics, isLoading: statsLoading } = useAuditStatisticsQuery();
    const cleanupMutation = useCleanupAuditLogsMutation();

    const handleExport = async () => {
        try {
            const blob = await AdminService.exportAuditCsv(filters);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `audit-log-${formatDatumIso(new Date())}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch {
            // Export failed - user will see no file downloaded
        }
    };

    const handleCleanup = () => {
        cleanupMutation.mutate(cleanupDays, {
            onSuccess: () => {
                setOpenCleanupDialog(false);
                refetch();
            },
        });
    };

    const columns: GridColDef[] = [
        {
            field: 'timestamp',
            headerName: 'Čas',
            width: 180,
            valueFormatter: (value: string) =>
                new Date(value).toLocaleString('cs-CZ'),
        },
        { field: 'userEmail', headerName: 'Uživatel', width: 200 },
        { field: 'action', headerName: 'Akce', width: 150 },
        { field: 'entityType', headerName: 'Entita', width: 120 },
        {
            field: 'description',
            headerName: 'Popis',
            flex: 1,
            minWidth: 200,
            renderCell: (params) => {
                const displayValue = !params.value || params.value === 'null' ? '—' : params.value;
                return (
                    <Tooltip title={displayValue}>
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {displayValue}
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Stav',
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={translateStatus(params.value)}
                    color={params.value === 'SUCCESS' ? 'success' : 'error'}
                    size="small"
                />
            ),
        },
        { field: 'ipAddress', headerName: 'IP', width: 130 },
    ];

    // Non-admin view
    if (!isAdmin) {
        return (
            <Box>
                <PageHeader title="Audit Log" />
                <Paper sx={{ p: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                        Pro přístup k audit logům jsou potřeba administrátorská oprávnění.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                title="Audit Log"
                action={
                    <Box display="flex" gap={1}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => refetch()}
                        >
                            Obnovit
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ExportIcon />}
                            onClick={handleExport}
                        >
                            Export CSV
                        </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<CleanupIcon />}
                            onClick={() => setOpenCleanupDialog(true)}
                        >
                            Vyčistit
                        </Button>
                    </Box>
                }
            />

            {/* Statistics */}
            {!statsLoading && statistics && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="h4">{statistics.totalCount}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Celkem záznamů
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="h4" color="success.main">
                                    {statistics.successCount}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Úspěšných
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="h4" color="error.main">
                                    {statistics.failureCount}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Neúspěšných
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="h4">{Object.keys(statistics.userCounts || {}).length}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Unikátních uživatelů
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            label="Akce"
                            fullWidth
                            size="small"
                            value={filters.action}
                            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            label="Typ entity"
                            fullWidth
                            size="small"
                            value={filters.entityType}
                            onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            label="Od data"
                            type="date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={filters.dateFrom}
                            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            label="Do data"
                            type="date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={filters.dateTo}
                            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Data Grid */}
            <Paper sx={{ width: '100%', overflowX: 'auto' }}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGrid
                        rows={auditLogs?.content || []}
                        columns={columns}
                        rowCount={auditLogs?.totalElements || 0}
                        paginationMode="server"
                        paginationModel={{ page, pageSize }}
                        onPaginationModelChange={(model) => {
                            setPage(model.page);
                            setPageSize(model.pageSize);
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        disableRowSelectionOnClick
                        localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
                        sx={{ height: 'calc(100vh - 320px)', minHeight: 400 }}
                    />
                )}
            </Paper>

            {/* Cleanup Dialog */}
            <Dialog open={openCleanupDialog} onClose={() => setOpenCleanupDialog(false)}>
                <DialogTitle>Vyčistit audit logy</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Smazat záznamy starší než:
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>Počet dní</InputLabel>
                        <Select
                            value={cleanupDays}
                            label="Počet dní"
                            onChange={(e) => setCleanupDays(e.target.value as number)}
                        >
                            <MenuItem value={30}>30 dní</MenuItem>
                            <MenuItem value={60}>60 dní</MenuItem>
                            <MenuItem value={90}>90 dní</MenuItem>
                            <MenuItem value={180}>180 dní</MenuItem>
                            <MenuItem value={365}>365 dní</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCleanupDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleCleanup}
                        disabled={cleanupMutation.isPending}
                    >
                        Vyčistit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AuditLogPage;
